import { AdaptiveColor } from './AdaptiveColor';
import { Color } from './Color';
import { ColorMode } from './ColorMode';
import { type IColor } from './IColor';

export enum ColorName {
  canvasFrame = 'canvasFrame',
  canvasBackground = 'canvasBackground',
  canvasGrid = 'canvasGrid',

  unitFrame = 'unitFrame',
  unitBackground = 'unitBackground',
  unitTitle = 'unitTitle',

  groupTitle = 'groupTitle',

  edge = 'edge',

  selectionHalo = 'selectionHalo',
  copyHighlight = 'copyHighlight',
  edgeEndpointHighlight = 'edgeEndpointHighlight',
  selectBoundsStroke = 'selectBoundsStroke',
  selectBoundsFill = 'selectBoundsFill',
  nodeCreationIndicatorStroke = 'nodeCreationIndicatorStroke',

  controlsBackground = 'controlsBackground',
  controlsForeground = 'controlsForeground'
}

type ColorMap = Map<ColorName, IColor>;

export class ColorScheme {
  private colorMap: ColorMap;

  constructor(colors: [ColorName, IColor][] | Map<ColorName, IColor>) {
    if (colors instanceof Map) {
      this.colorMap = colors;
    } else {
      this.colorMap = new Map(colors);
    }
  }

  get(name: ColorName, mode: ColorMode): Color {
    const result = this.colorMap.get(name);
    if (result === undefined) {
      throw new Error(`Color ${name} not found in color scheme`);
    }
    if (result instanceof Color) {
      return result;
    }
    console.warn(`Adaptive color ${name} not realized in color scheme`);
    return result.realize(mode);
  }

  getAdaptive(name: ColorName): IColor {
    const result = this.colorMap.get(name);
    if (result === undefined) {
      throw new Error(`Color ${name} not found in color scheme`);
    }
    return result;
  }

  realize(mode: ColorMode): ColorScheme {
    const newColors = new Map<ColorName, IColor>();
    for (const [name, color] of this.colorMap) {
      newColors.set(name, color.realize(mode));
    }
    return new ColorScheme(Array.from(newColors.entries()));
  }

  lerp(t: number, end: ColorScheme, mode: ColorMode): ColorScheme {
    const newColors = new Map<ColorName, IColor>();
    for (const [name, color] of this.colorMap) {
      newColors.set(name, color.lerp(t, end.get(name, mode)));
    }
    return new ColorScheme(newColors);
  }

  interp(end: ColorScheme, mode: ColorMode): (t: number) => ColorScheme {
    return (t: number) => this.lerp(t, end, mode);
  }
}

export const colorScheme = new ColorScheme([
  [ColorName.canvasFrame, AdaptiveColor.GRAY_5],
  [
    ColorName.canvasBackground,
    new AdaptiveColor(Color.from8Bit(190, 190, 194), Color.from8Bit(44, 44, 46))
  ],
  [ColorName.canvasGrid, AdaptiveColor.BLUE],

  [ColorName.unitFrame, AdaptiveColor.SECONDARY_FOREGROUND],
  [ColorName.unitBackground, AdaptiveColor.SECONDARY_BACKGROUND],
  [ColorName.unitTitle, AdaptiveColor.FOREGROUND],

  [ColorName.groupTitle, AdaptiveColor.FOREGROUND],

  [ColorName.edge, AdaptiveColor.SECONDARY_FOREGROUND],

  [
    ColorName.selectionHalo,
    new AdaptiveColor(
      AdaptiveColor.GREEN.light.lighten(0.25),
      AdaptiveColor.GREEN.dark.darken(0.25)
    )
  ],
  [ColorName.copyHighlight, AdaptiveColor.ORANGE],
  [ColorName.edgeEndpointHighlight, AdaptiveColor.PURPLE],

  [ColorName.selectBoundsStroke, AdaptiveColor.GRAY],
  [ColorName.selectBoundsFill, AdaptiveColor.GRAY],
  [
    ColorName.nodeCreationIndicatorStroke,
    new AdaptiveColor(AdaptiveColor.ORANGE.light.darken(0.1), AdaptiveColor.ORANGE.dark)
  ],

  [ColorName.controlsBackground, AdaptiveColor.SECONDARY_BACKGROUND],
  [ColorName.controlsForeground, AdaptiveColor.SECONDARY_FOREGROUND]
]);

export function colorSchemeForMode(mode: ColorMode): ColorScheme {
  switch (mode) {
    case ColorMode.LIGHT:
      return colorScheme.realize(ColorMode.LIGHT);
    case ColorMode.DARK:
      return colorScheme.realize(ColorMode.DARK);
  }
}

export function getColor(name: ColorName, mode: ColorMode): Color {
  return colorSchemeForMode(mode).get(name, mode);
}

export function getAdaptiveColor(name: ColorName): IColor {
  return colorScheme.getAdaptive(name);
}
